// Typed wrapper around the FastAPI surface. The session cookie is set by
// the api on POST /api/auth/login; subsequent fetches just need
// `credentials: "include"`.

import type { Lens, Pose } from "./fisheye/lens";

// Staff (IntraScene): viewer < admin < dev. Customer people: viewer < owner.
export type Role = "viewer" | "owner" | "admin" | "dev";

export type Account = { id: number; name: string };

export type Me = {
  username: string;
  /** Effective role: a staff member viewing a customer acts as its owner. */
  role: Role;
  real_role: Role;
  /** IntraScene staff acting as staff (not viewing a customer). */
  is_staff: boolean;
  /** The customer account this session sees (own, or the one being viewed). */
  customer: Account | null;
  viewing_as: boolean;
};

export type Customer = Account & {
  created_at: string;
  locations: number;
  boxes: number;
  people: number;
};

export type Person = {
  id: number;
  username: string;
  role: Role;
  customer_id: number | null;
  customer_name: string;
  email?: string | null;
  created_at: string;
};

export type CameraRate = {
  id: string;
  name: string;
  role: string;
  main_bps: number | null;
  sub_bps: number | null;
  estimated: boolean;
};

export type SiteBandwidth = {
  store: string;
  display_name: string;
  uplink_mbps: number | null;
  cameras: CameraRate[];
  analysis_bps: number;
  unmeasured: number;
  needed_mbps: number;
  status: "ok" | "tight" | "over" | "unknown";
};

/** A worker's whole GPU as of its last heartbeat (every process on the card). */
export type GpuStats = {
  util_pct: number | null;
  mem_used_mib: number | null;
  mem_total_mib: number | null;
  dec_pct: number | null;
  enc_pct: number | null;
  temp_c: number | null;
  power_w: number | null;
};

export type WorkerGpu = {
  id: string;
  gpu: string;
  live: boolean;
  age_s: number;
  cameras: number;
  capacity: number;
  stats: GpuStats | null;
  stats_age_s: number | null;
};

export type InviteOut = { link: string; emailed: boolean; expires_at: string };
export type InviteInfo = { business_name: string; role: Role; email: string };
export type SignupBody = {
  serial: string;
  claim_code: string;
  business_name: string;
  location_name: string;
  username: string;
  password: string;
  email?: string;
};

export type BoxState = "waiting" | "reconnecting" | "online";

export type AuditEntry = {
  id: number;
  ts: string;
  username: string;
  action: string;
  customer_id: number | null;
  details: Record<string, unknown> | null;
};

export type Camera = {
  id: string;
  name: string;
  rtsp_url: string;
  // Lower-bitrate second stream from the recorder, when it exposes one.
  sub_rtsp_url?: string;
  nvr_id?: string | null;
  channel?: number | null;
  fps: number;
  sample_interval_s: number;
  enabled: boolean;
  store?: string;
  // Pipeline cost: "ai" cameras are detected + tracked and take one of the
  // AI batch slots; "view_only" cameras are decode + preview only.
  role?: "ai" | "view_only";
  // Source-resolution ceiling in pixels of height; 0 = unlimited.
  max_height?: number;
  // Frames per second analysed (detection + tracking); 0 = system default.
  ingest_max_fps?: number;
  // null/undefined = inherit the system Descriptions setting.
  people_description?: boolean | null;
  activity_description?: boolean | null;
  created_at?: string;
  // Recorder fisheye: extra NVR channels for PTZ / panorama views.
  dewarped_channels?: number[];
  // Fisheye calibration, on the fisheye camera itself.
  lens?: Lens | null;
  // On a view cut from a fisheye: the fisheye's id and where the view looks.
  parent_id?: string | null;
  view?: Pose | null;
};

// AI vs non-AI camera counts and their ceilings. AI cameras are limited both
// fleet-wide and per store; non-AI cameras are a relay and effectively free.
export type CameraCapacity = {
  ai_used: number;
  ai_max: number | null;
  ai_max_per_store?: number | null;
  ai_per_store?: Record<string, number>;
  view_only_used: number;
  view_only_max: number;
  // AI cameras configured but not analysed because the fleet is over its
  // limits, keyed by camera id with the reason. Normally empty.
  over_limit?: Record<string, string>;
};

export type SimOffset = {
  label: string;
  offset: string;
};

export type Store = {
  name: string;
  display_name: string;
  location: string;
  is_simulation: boolean;
  sim_videos_subdir: string;
  sim_start_time: string;
  sim_loop: boolean;
  sim_live: boolean;
  sim_offsets: SimOffset[];
  sim_copies: number;
  sim_finished: boolean;
  min_crop_area_px2: number;
  crop_expand_pct: number;
  ai_ingest: boolean;
  live_hls_fallback: boolean;
  camera_count: number;
  ai_camera_count: number;
  nvr_count: number;
  customer_id: number | null;
  customer_name?: string;
};

export type SimVideoFolder = { name: string; clips: number };

export type Stores = {
  stores: Store[];
  sim_videos: SimVideoFolder[];
  sim_library_mounted: boolean;
};

export type StoreInput = {
  name: string;
  display_name?: string;
  location?: string;
  is_simulation?: boolean;
  sim_videos_subdir?: string;
  sim_start_time?: string;
  sim_loop?: boolean;
  sim_live?: boolean;
  sim_offsets?: SimOffset[];
  sim_copies?: number;
  min_crop_area_px2?: number;
  crop_expand_pct?: number;
  ai_ingest?: boolean;
  live_hls_fallback?: boolean;
  customer_id?: number | null;
};

export type StoreUpdate = {
  name?: string;
  display_name?: string;
  location?: string;
  is_simulation?: boolean;
  sim_videos_subdir?: string;
  sim_start_time?: string;
  sim_loop?: boolean;
  sim_live?: boolean;
  sim_offsets?: SimOffset[];
  sim_copies?: number;
  min_crop_area_px2?: number;
  crop_expand_pct?: number;
  ai_ingest?: boolean;
  live_hls_fallback?: boolean;
  customer_id?: number | null;
};

// A named, reusable camera group. Members may come from different stores and
// keep their own store context wherever the profile is used.
export type CameraProfile = {
  id: string;
  name: string;
  description: string;
  camera_ids: string[];
  members: Array<{
    camera_id: string;
    name: string;
    store: string;
    role: string;
    enabled: boolean;
  }>;
  // Ids stored on the profile whose camera no longer exists.
  missing: string[];
  stores: string[];
  created_at?: string;
  customer_id?: number | null;
};

export type CameraProfileInput = {
  id?: string;
  name: string;
  description?: string;
  camera_ids: string[];
  customer_id?: number | null;
};

// Fleet AI-camera ceilings. Readable by everyone (so a refusal is
// explainable), editable by dev only.
export type CapacityLimits = {
  max_ai_cameras: number;
  max_ai_cameras_configured: number;
  max_ai_cameras_per_store: number;
  engine_ai_ceiling: number | null;
  view_only_max: number;
  ai_used: number;
  view_only_used: number;
  ai_per_store: Record<string, number>;
  editable: boolean;
};

export type ScopedPurgeResult = {
  status: string;
  store?: string;
  camera_ids: string[];
  qdrant_tracks_cleared: boolean;
  minio_objects_deleted: number;
};

export type Nvr = {
  id: string;
  name: string;
  brand: string;
  host: string;
  port: number;
  username: string;
  // True when the server has a stored password for this NVR. The raw
  // password is never returned.
  password_set: boolean;
  rtsp_url_template: string;
  store?: string;
  // Whether Blinkfree knows this brand's second (sub) stream URL shape.
  supports_sub_stream?: boolean;
  // Reached through this site connector; host/port are then its LAN address.
  connector_id?: string | null;
  // Where Blinkfree services connect (the overlay port behind a connector).
  dial_host?: string;
  dial_port?: number;
  created_at?: string;
};

// Input for create/update. `password` is optional on update: undefined or
// null = keep the existing stored value, an empty string clears it.
export type NvrInput = {
  id: string;
  name: string;
  brand: string;
  host: string;
  port: number;
  username: string;
  password?: string | null;
  rtsp_url_template?: string | null;
  store?: string;
  connector_id?: string | null;
};

export type DiscoveredDevice = {
  ip: string;
  rtsp_port?: number;
  rtsp_server?: string;
  brand_hint?: string;
  onvif?: {
    xaddr: string;
    types: string;
    name: string;
    hardware: string;
    manufacturer: string;
    mac: string;
    type: string;
  };
};

export type ConnectorFacts = {
  uptime_s?: number;
  temp_c?: number | null;
  throttled?: string;
  model?: string;
  lan?: { name: string; ip: string; prefix: number }[];
};

export type Connector = {
  id: string;
  name: string;
  // "" while in stock (provisioned, not yet at a store).
  store: string;
  // Product serial printed on the box, e.g. ISC-2610-0001.
  serial: string;
  claimed_at: string | null;
  hardware: { board_serial?: string; mac?: string; model?: string } | null;
  overlay_ip: string;
  hostname: string;
  version: string;
  // How it reaches this system: "direct" | "relay" | "idle"; "" before it reports.
  path: string;
  online: boolean;
  registered: boolean;
  enrollment_expires_at: string | null;
  last_seen_at: string | null;
  facts: ConnectorFacts | null;
  discovered: {
    scanned_at: number;
    duration_s: number;
    networks: string[];
    devices: DiscoveredDevice[];
  } | null;
  discover_requested: boolean;
  recorder_ids: string[];
  created_at: string;
  // Owning customer account; null = IntraScene stock / staff.
  customer_id: number | null;
  customer_name?: string;
};

// Where a box is being placed: an existing store, or (customers) a new
// location created for it.
export type BoxPlacement = { store?: string; location_name?: string; name?: string };

// Downloaded once as enroll.json and copied to the device.
export type EnrollmentBundle = {
  connector_id: string;
  enroll_token: string;
  api_url: string;
  headscale_url: string;
  preauth_key: string;
  expires_at: string;
};

export type PipelineActivity = {
  cameras_total: number;
  cameras_enabled: number;
  /** Frames/sec reaching the tracker probe (decode + infer + track). */
  decode_fps: number | null;
  /** Crop messages/sec published to Redis (people passing the sampling gate). */
  crops_fps: number | null;
  /** @deprecated use crops_fps — kept for stale bundles */
  ingest_fps: number | null;
  state: "streaming" | "idle" | "no_cameras" | "unknown";
};

// Returned by POST / PATCH / DELETE on /api/cameras. Writes apply to the
// running pipeline LIVE; `apply_errors` carries per-camera refusals (e.g. the
// AI batch is full) while the row is always persisted regardless.
export type CameraWriteResult = {
  camera: Camera | null;
  applied: boolean;
  apply_errors: string[];
};

export type ActivityEntry = {
  ts_ms: number;
  activity: string;
};

export type TrackHit = {
  track_key: string;
  camera_id: string;
  track_id: string;
  first_seen_utc: string;
  last_seen_utc: string;
  frame_url: string;
  bbox: number[];
  summary: string;
  caption_prose?: string;
  matching_score: number; // 0–100 integer; max of dense cosine % and sparse lexical %
  /** @deprecated use matching_score */
  score?: number;
  num_observations: number;
  /** Latest one-line activity (Q10), when the backend has it. */
  activity?: string;
  activities_distinct: string[];
  activity_history: ActivityEntry[];
  attributes: Record<string, unknown>;
};

export type SearchSort = "relevance" | "recency";

/** Which embedding space(s) the query is compared against. */
export type SearchMode = "both" | "appearance" | "activity";

// Request a recorded video snippet. `pre_s`/`post_s` are seconds to grab
// before start_ms / after end_ms (clamped 0..600 server side; total clip
// <= 1200s). For a point on a camera's timeline, pass the same instant as
// start_ms and end_ms and let pre/post define the window.
export type ClipRequest = {
  camera_id: string;
  start_ms: number;
  end_ms: number;
  pre_s: number;
  post_s: number;
  track_key?: string;
  // NVR pull rate (RTSP Scale). 1 = real time. Simulated clips ignore it.
  replay_rate?: number;
  // Stops the clip-service job when the browser aborts or the user pauses.
  job_id?: string;
  // Write this request onto the store's saved-clips list after success.
  save_download?: boolean;
  // Search frame or alert crop kept as the saved-clip thumbnail.
  thumb_url?: string;
  // For a fisheye: the view on screen, so the clip is that view.
  view?: ClipView;
};

/** A fisheye view as framed on screen: where it looks and the frame's shape. */
export type ClipView = Pose & { aspect: number };

export const CLIP_MAX_SIDE_S = 600;
export const CLIP_MAX_DURATION_S = 1200;
/** NVR replay pull speed when the operator has not picked one. */
export const DEFAULT_REPLAY_RATE = 16;

export type SavedClip = {
  id: number;
  store: string;
  camera_id: string;
  camera_name: string;
  start_ms: number;
  end_ms: number;
  pre_s: number;
  post_s: number;
  replay_rate: number;
  begin_unix: number;
  end_unix: number;
  duration_s: number;
  filename: string;
  simulated: boolean;
  has_thumb: boolean;
  kind?: "clip" | "image";
  created_at: string;
  // The fisheye view the clip was cut to; null for an ordinary camera or a
  // whole-fisheye clip.
  view?: ClipView | null;
};

export type SavedClipsPage = {
  clips: SavedClip[];
  retention_days: number;
};

export type SavedClipRecord = {
  camera_id: string;
  start_ms: number;
  end_ms: number;
  pre_s: number;
  post_s: number;
  replay_rate?: number;
  begin_unix: number;
  end_unix: number;
  duration_s: number;
  filename: string;
  simulated: boolean;
  thumb_url?: string;
  kind?: "clip" | "image";
};

export type ClipResult = {
  url: string;
  filename: string;
  camera_id: string;
  begin_unix: number;
  end_unix: number;
  duration_s: number;
  // Footage came from the simulation library, not a recorder.
  simulated: boolean;
  // Seconds into the MP4 of the still this clip was opened from.
  moment_offset_s?: number;
};

export type ReplayPlay = {
  path: string;
  transport: string;
  begin_unix: number;
  end_unix: number;
  simulated: boolean;
};

// Where a camera's recorded footage would come from, so the UI can say why a
// playback control is unavailable before the operator presses it.
export type PlaybackSource = "nvr" | "simulation" | "none";

export type CameraActivityAlert = {
  ts_ms: number;
  kind: string;
  text: string;
  alert_id?: string;
};

// Activity density along a camera's timeline. `detections` is one count per
// bucket, in order; the UI scales it against its own maximum because an
// absolute count means nothing without the camera's usual traffic.
export type CameraActivity = {
  camera_id: string;
  from_ms: number;
  to_ms: number;
  bucket_ms: number;
  detections: number[];
  alerts: CameraActivityAlert[];
  total_detections: number;
  truncated: boolean;
  playback: PlaybackSource;
};

export type WeaponAlertStatus = "pending" | "confirmed" | "cleared";

export type WeaponAlert = {
  alert_id: string;
  corr_id: string;
  camera_id: string;
  track_id: number;
  status: WeaponAlertStatus;
  crop_url: string;
  frame_url?: string;
  /** Full mux frame is stored (tmpfs and/or MinIO key). Omits the crop/frame switch when false. */
  has_frame?: boolean;
  initial_answer: string;
  verify_answer: string | null;
  created_at_ms: number;
  updated_at_ms: number;
  acquired_at_ms?: number | null;
  pending_visible_until_ms?: number | null;
  // "weapon" (built-in) or "policy" (per-camera watchbox). A policy alert
  // shows track_label (name), store + camera, then alert_text (message).
  kind?: "weapon" | "policy";
  policy_id?: string | null;
  store?: string;
  alert_text?: string;
  track_label?: string;
};

export type PolicySource = "region" | "detection";

export type Policy = {
  id: string;
  camera_id: string;
  store?: string;
  name: string;
  enabled: boolean;
  source: PolicySource;
  // Normalized xyxy in [0, 1]; required for region policies.
  bbox?: number[] | null;
  interval_s: number;
  cooldown_s: number;
  prompt1: string;
  prompt2?: string | null;
  // True: prompt2 is a YES/NO gate that confirms or clears the alert.
  // False: prompt2 is descriptive and only annotates a confirmed alert.
  prompt2_yes_no?: boolean;
  alert_text: string;
  created_at?: string;
};

// Input for create/update. `id` is server-generated on create.
export type PolicyInput = {
  camera_id: string;
  store?: string;
  name?: string;
  enabled?: boolean;
  source: PolicySource;
  bbox?: number[] | null;
  interval_s: number;
  cooldown_s: number;
  prompt1: string;
  prompt2?: string | null;
  prompt2_yes_no?: boolean;
  alert_text: string;
};

export const ALERTS_PAGE_SIZE = 75;

export type WeaponAlertsPage = {
  limit?: number;
  pendingOffset?: number;
  confirmedOffset?: number;
  historyOffset?: number;
};

export type WeaponAlertsPanel = {
  pending: WeaponAlert[];
  confirmed: WeaponAlert[];
  history: WeaponAlert[];
  pending_total: number;
  confirmed_total: number;
  history_has_more: boolean;
  limit: number;
  pending_offset: number;
  confirmed_offset: number;
  history_offset: number;
};

export type DashboardRange = "24h" | "7d" | "30d" | "all";

export type DashboardDemographicBucket = {
  label: string;
  count: number;
};

export type DashboardTimeBucket = {
  bucket_start_ms: number;
  count: number;
};

export type DashboardStats = {
  range: DashboardRange;
  from_ms: number | null;
  to_ms: number;
  total_customers: number;
  demographics: {
    sex: DashboardDemographicBucket[];
    age_group: DashboardDemographicBucket[];
  };
  over_time: DashboardTimeBucket[];
};

export type FleetCamera = {
  id: string;
  name: string;
  store: string;
  role: string;
  nvr_id: string | null;
  device_id: string;
  device_name: string;
  state: string;
  healthy: boolean;
  health_reason: string;
  auto_connect: boolean;
  attached: boolean;
  recovering: boolean;
  recovery_attempts: number;
  recoveries: number;
  recover_max_attempts: number;
  next_retry_at: number | null;
  next_retry_in_s: number | null;
  seconds_since_frame: number | null;
  last_error: string;
  connection_issue: boolean;
};

export type FleetDevice = {
  id: string;
  kind: "nvr" | "host" | "connector";
  name: string;
  host: string;
  port: number;
  status: "ok" | "drop" | "down";
  camera_count: number;
  unhealthy_count: number;
  camera_ids: string[];
  // Recorder behind a connector: the connector's name.
  via?: string;
  // Connector only.
  last_seen_at?: string | null;
  path?: string;
};

export type FleetDrop = {
  ts: string;
  event_type: string;
  camera_id: string | null;
  camera_name: string;
  device_id: string;
  device_name: string;
  reason: string;
};

export type FleetHealth = {
  cameras: FleetCamera[];
  devices: FleetDevice[];
  recent_drops: FleetDrop[];
  pipeline: string;
};

// A region drawn on one camera's frame. `bbox` is normalized xyxy against the
// displayed image, so the browser's render size never affects the query.
export type SpatialFilter = {
  camera_id: string;
  bbox: [number, number, number, number];
  // Fraction of an object's own area that must fall inside the region.
  min_overlap: number;
  at_ts?: string;
};

export type SearchBody = {
  q: string;
  camera_ids?: string[];
  from_ts?: string;
  to_ts?: string;
  limit?: number;
  sort?: SearchSort;
  min_score?: number;
  search_mode?: SearchMode;
  exclusive?: boolean;
  activity?: string;
  sex?: string;
  upper_color?: string;
  lower_color?: string;
  accessories?: string[];
  spatial?: SpatialFilter | null;
  balance_cameras?: boolean;
};

// A frame to draw a spatial region on. `source_width/height` is the pixel
// space stored detection boxes live in.
export type FrameAt = {
  camera_id: string;
  frame_url: string;
  frame_id: string;
  acquired_at_utc: string | null;
  source_width: number;
  source_height: number;
  kind: "stored" | "live";
  detections: Array<{
    track_key: string;
    track_id: string;
    bbox: number[];
    summary: string;
  }>;
};

// Live numbers fed into the Status page Quick Stats strip. Each value may
// be `null` when the underlying metric is missing (e.g. on cold start, or
// before a stage has produced any items). The frontend renders `--` for
// nulls instead of failing the whole strip.
export type QuickStats = {
  ingestFps: number | null;
  captionFps: number | null;
  writeFps: number | null;
  dropRate: number | null;
  inFlightCaptioner: number | null;
  captionerP95: number | null;
  endToEndP95: number | null;
  gpuUtil: number | null;
  activeTracks: number | null;
  /** Unread crops waiting for captioner (consumer lag, not XLEN). */
  cropsQueue: number | null;
  /** Unread tracks waiting for embedder. */
  tracksQueue: number | null;
  /** Unread to_db records waiting for db_writer. */
  toDbQueue: number | null;
  /** Unread weapon-verify crops (captioner priority stream). */
  weaponVerifyQueue: number | null;
  /** Retained entries on the crops stream (XLEN / maxlen cap). */
  cropsBuffer: number | null;
  tracksBuffer: number | null;
  toDbBuffer: number | null;
  weaponVerifyBuffer: number | null;
  /** Dead-letter stream (failed_items). */
  failedItemsBuffer: number | null;
};

export type PurgeResult = {
  status: string;
  qdrant_tracks_cleared: boolean;
  qdrant_detections_dropped: boolean;
  minio_objects_deleted: number;
  redis_streams_trimmed: string[];
  inflight_files_deleted: number;
};

export type StoragePurgeDaysResult = {
  status: string;
  days: number;
  retention_days: number;
  start_date: string;
  end_date: string;
  frames_deleted: number;
  clips_deleted: number;
  snapshots_deleted: number;
  pg_backups_deleted: number;
  qdrant_tracks_cleared: boolean;
};

export type StorageCamera = {
  camera_id: string;
  name: string;
  bytes: number;
  objects: number;
};

export type StorageStore = {
  store: string;
  bytes: number;
  objects: number;
  cameras: StorageCamera[];
};

export type StoragePolicy = {
  max_bytes: number;
  retention_days: number;
  clip_retention_hours: number;
  qdrant_snapshot_keep_days: number;
  project_max_bytes: number;
};

export type StorageWarning = {
  history_days_kept: number;
  retention_days: number;
  used_bytes: number;
  max_bytes: number;
  limit?: "detection" | "project" | "both";
};

export type StorageUsage = {
  as_of_ms?: number;
  total_bytes: number;
  buckets: {
    frames: { bytes: number; objects: number };
    clips: { bytes: number; objects: number };
  };
  other_bytes: number;
  other_objects: number;
  stores: StorageStore[];
  stores_ready?: boolean;
  policy: StoragePolicy;
  history_days_kept: number | null;
  warning: StorageWarning | null;
};

export type StorageHistoryRange = "24h" | "7d" | "30d" | "all";

export type StorageHistoryPoint = {
  ts_ms: number;
  total_bytes: number;
  frames_bytes: number;
  clips_bytes: number;
};

export type StorageHistory = {
  range: StorageHistoryRange;
  policy: StoragePolicy;
  points: StorageHistoryPoint[];
};

export type EventSeverity = "info" | "warning" | "error" | "critical";

export type EventRow = {
  id: number;
  ts: string;
  service: string;
  severity: EventSeverity;
  event_type: string;
  camera_id: string | null;
  reason: string;
  details: Record<string, unknown>;
  actor: string | null;
};

export type EventsPage = {
  events: EventRow[];
  next_cursor: string | null;
};

export type EventFilters = {
  from_ts?: string;
  to_ts?: string;
  service?: string;
  severity?: string;
  event_type?: string;
  camera_id?: string;
  q?: string;
  cursor?: string;
  limit?: number;
};

export type HostBoot = { index: number; boot_id: string; raw: string };

export type HostTriage = {
  boot_time_epoch: number | null;
  uptime_seconds: number | null;
  dirty_bit_set: boolean | null;
  kernel_crash_markers: string[];
  boots: HostBoot[];
  verdict: "clean" | "abrupt" | "unknown";
  detail: string;
  note: string;
};

export type HostChecklistItem = {
  id: string;
  label: string;
  value: unknown;
  ok: boolean | null;
  detail: string;
};

export type HostChecklist = { available: boolean; items: HostChecklistItem[] };

export type HostContainer = {
  container: string;
  status?: string;
  started_at?: string;
  finished_at?: string;
  restart_count?: number;
  exit_code?: number;
  oom_killed?: boolean;
  error?: string;
};

export type CrashWindow = {
  start: string;
  end: string;
  step_s: number;
  series: Record<string, Array<[number, number]>>;
};

// What the user is currently looking at. Sent explicitly on every scoped
// request instead of being a server-side global, so two people can work on
// different stores at the same time.
export type Scope = { store?: string; profileId?: string };

function scopeQuery(
  scope?: Scope,
  extra?: Record<string, string | number | undefined>,
): string {
  const q = new URLSearchParams();
  if (scope?.store) q.set("scope_store", scope.store);
  if (scope?.profileId) q.set("scope_profile", scope.profileId);
  if (extra) {
    for (const [key, value] of Object.entries(extra)) {
      if (value === undefined) continue;
      q.set(key, String(value));
    }
  }
  const qs = q.toString();
  return qs ? `?${qs}` : "";
}

export class ApiError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
  }
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const r = await fetch(path, {
    credentials: "include",
    headers: { "content-type": "application/json", ...(init?.headers || {}) },
    ...init,
  });
  if (
    r.status === 401 &&
    !path.startsWith("/api/auth/") &&
    window.location.pathname !== "/login"
  ) {
    // Session cookie expired mid-session (8h TTL). Without this the app
    // just breaks in place with raw '{"detail": ...}' errors and the user
    // has no idea they need to re-authenticate.
    window.location.assign("/login");
    throw new ApiError(401, "Session expired — redirecting to login");
  }
  if (!r.ok) {
    const text = await r.text().catch(() => "");
    // FastAPI error bodies are {"detail": "..."} for our own errors, but a
    // VALIDATION failure sends {"detail": [{loc, msg, ...}]}. Only handling the
    // string form left request-shape bugs showing as a generic apology with the
    // real cause — which field, and why — sitting unread in the response.
    let message = text || r.statusText;
    try {
      const parsed = JSON.parse(text);
      const detail = parsed?.detail;
      if (typeof detail === "string") {
        message = detail;
      } else if (Array.isArray(detail)) {
        message = detail
          .map((d) => {
            const field = Array.isArray(d?.loc) ? d.loc[d.loc.length - 1] : "";
            return field ? `${field}: ${d?.msg}` : String(d?.msg ?? "");
          })
          .filter(Boolean)
          .join("; ");
      }
    } catch {
      // not JSON; keep raw text
    }
    throw new ApiError(r.status, message);
  }
  if (r.status === 204) return undefined as T;
  return (await r.json()) as T;
}

export type ModelProfile = {
  id: string;
  label: string;
  model: string;
  quant: string;
  gpu_mem: number;
  max_model_len: number;
  max_num_seqs: number;
  extra_args: string;
};

export type CaptionModels = {
  models: ModelProfile[];
  active_id: string | null;
  configured_model: string;
  served: string[];
  vllm: string;
};

export type NotificationSeverity = "info" | "warning" | "critical";

export type AppNotification = {
  id: number;
  created_at: string;
  severity: NotificationSeverity;
  kind: string;
  title: string;
  message: string;
  read_at: string | null;
  dismissed_at: string | null;
};

export const api = {
  login: (username: string, password: string) =>
    call<Me>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ username, password }),
    }),
  logout: () => call<{ status: string }>("/api/auth/logout", { method: "POST" }),
  signupWithBox: (body: SignupBody) =>
    call<Me>("/api/auth/signup-with-box", { method: "POST", body: JSON.stringify(body) }),
  boxStatus: (serial: string, claim_code: string) =>
    call<{ state: BoxState }>("/api/auth/box-status", {
      method: "POST",
      body: JSON.stringify({ serial, claim_code }),
    }),
  invites: {
    info: (token: string) => call<InviteInfo>(`/api/auth/invites/${encodeURIComponent(token)}`),
    accept: (token: string, username: string, password: string) =>
      call<Me>("/api/auth/invites/accept", {
        method: "POST",
        body: JSON.stringify({ token, username, password }),
      }),
  },
  passwordReset: {
    request: (login: string) =>
      call<void>("/api/auth/password-reset", { method: "POST", body: JSON.stringify({ login }) }),
    confirm: (token: string, new_password: string) =>
      call<void>("/api/auth/password-reset/confirm", {
        method: "POST",
        body: JSON.stringify({ token, new_password }),
      }),
  },
  me: () => call<Me>("/api/me"),
  viewAs: (customer_id: number | null) =>
    call<Me>("/api/auth/view-as", {
      method: "POST",
      body: JSON.stringify({ customer_id }),
    }),
  customers: {
    list: () => call<Customer[]>("/api/customers"),
    create: (name: string) =>
      call<Customer>("/api/customers", { method: "POST", body: JSON.stringify({ name }) }),
    rename: (id: number, name: string) =>
      call<Customer>(`/api/customers/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ name }),
      }),
    delete: (id: number) =>
      call<{ status: string }>(`/api/customers/${id}`, { method: "DELETE" }),
    auditLog: (customer_id?: number) =>
      call<AuditEntry[]>(
        `/api/audit-log${customer_id != null ? `?customer_id=${customer_id}` : ""}`,
      ),
  },
  people: {
    list: () => call<Person[]>("/api/users"),
    create: (body: {
      username: string;
      password: string;
      role: Role;
      email?: string;
      customer_id?: number | null;
    }) => call<Person>("/api/users", { method: "POST", body: JSON.stringify(body) }),
    invite: (body: { role: Role; email: string; customer_id?: number | null }) =>
      call<InviteOut>("/api/users/invites", { method: "POST", body: JSON.stringify(body) }),
    update: (id: number, body: { role?: Role; password?: string; email?: string }) =>
      call<Person>(`/api/users/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
    delete: (id: number) =>
      call<{ status: string }>(`/api/users/${id}`, { method: "DELETE" }),
  },
  changePassword: (current_password: string, new_password: string) =>
    call<void>("/api/auth/change-password", {
      method: "POST",
      body: JSON.stringify({ current_password, new_password }),
    }),
  search: (body: SearchBody, scope?: Scope) =>
    call<TrackHit[]>(`/api/search${scopeQuery(scope)}`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  // A representative frame for a camera near a moment in time, used to draw a
  // spatial search region. Falls back to a live snapshot when the archive has
  // nothing close, and reports which it gave you.
  frameAt: (
    cameraId: string,
    at: Date,
    opts?: { storedOnly?: boolean; maxWindowMs?: number },
  ) => {
    const q = new URLSearchParams({
      camera_id: cameraId,
      at: at.toISOString(),
    });
    if (opts?.storedOnly) q.set("stored_only", "true");
    if (opts?.maxWindowMs) q.set("max_window_ms", String(opts.maxWindowMs));
    return call<FrameAt>(`/api/frames/at?${q}`);
  },
  clip: (body: ClipRequest, signal?: AbortSignal) =>
    call<ClipResult>("/api/search/clip", {
      method: "POST",
      body: JSON.stringify(body),
      signal,
    }),
  cancelClip: (jobId: string) =>
    call<{ status: string; job_id: string }>(
      `/api/search/clip/jobs/${encodeURIComponent(jobId)}`,
      { method: "DELETE" },
    ).catch(() => undefined),
  downloads: {
    list: (scope?: Scope) =>
      call<SavedClipsPage>(`/api/downloads${scopeQuery(scope)}`),
    record: (body: SavedClipRecord) =>
      call<SavedClip>("/api/downloads", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    thumbUrl: (id: number, download = false) =>
      `/api/downloads/${id}/thumb${download ? "?download=1" : ""}`,
    remove: (id: number, scope?: Scope) =>
      call<{ status: string; id: number }>(
        `/api/downloads/${id}${scopeQuery(scope)}`,
        { method: "DELETE" },
      ),
  },
  // Recordings page: remux the recorder (or sim file) from this instant and
  // return a live HLS path. Not a finished MP4 — that wait tracked the Play
  // window, not seek cost.
  recordings: {
    play: (
      body: { camera_id: string; start_ms: number },
      signal?: AbortSignal,
    ) =>
      call<ReplayPlay>(`/api/recordings/play`, {
        method: "POST",
        body: JSON.stringify(body),
        signal,
      }),
    stop: (cameraId: string) =>
      call<{ path: string; removed: boolean }>(
        `/api/recordings/play/${encodeURIComponent(cameraId)}`,
        { method: "DELETE" },
      ),
    thumbUrl: (cameraId: string, atMs: number) => {
      const q = new URLSearchParams({
        camera_id: cameraId,
        at: new Date(atMs).toISOString(),
      });
      return `/api/recordings/thumb?${q}`;
    },
  },
  // Authorises a viewer for the bitstream relay and returns an opaque session
  // id. One session serves every seek, so a scrub costs a WebSocket message
  // rather than a fresh connection — which matters because a cold TCP
  // connection spends several round trips in slow start before it can carry a
  // 4K keyframe.
  streams: {
    open: (
      body: { camera_id: string; kind: "live" | "replay"; stream?: "main" | "sub" },
      signal?: AbortSignal,
    ) =>
      call<{ session: string; kind: string; camera_id: string }>(`/api/streams`, {
        method: "POST",
        body: JSON.stringify(body),
        signal,
      }),
  },
  cameras: {
    list: (scope?: Scope) => call<Camera[]>(`/api/cameras${scopeQuery(scope)}`),
    capacity: () => call<CameraCapacity>("/api/cameras/capacity"),
    // Activity density for a timeline window. Buckets are counts only — the
    // timeline stays clickable everywhere, activity or not.
    activity: (
      cameraId: string,
      from: Date,
      to: Date,
      buckets: number,
      scope?: Scope,
    ) => {
      const q = new URLSearchParams({
        from_ts: from.toISOString(),
        to_ts: to.toISOString(),
        buckets: String(buckets),
      });
      if (scope?.store) q.set("scope_store", scope.store);
      if (scope?.profileId) q.set("scope_profile", scope.profileId);
      return call<CameraActivity>(
        `/api/cameras/${encodeURIComponent(cameraId)}/activity?${q}`,
      );
    },
    upsert: (cam: Camera) =>
      call<CameraWriteResult>("/api/cameras", {
        method: "POST",
        body: JSON.stringify(cam),
      }),
    update: (cam: Camera) =>
      call<CameraWriteResult>(`/api/cameras/${cam.id}`, {
        method: "PATCH",
        body: JSON.stringify(cam),
      }),
    delete: (id: string) =>
      call<CameraWriteResult>(`/api/cameras/${id}`, { method: "DELETE" }),
    // Fisheye: calibration (null clears it) and its AI views, each analysed as
    // its own AI camera.
    setLens: (id: string, lens: Lens | null) =>
      call<CameraWriteResult>(`/api/cameras/${encodeURIComponent(id)}/lens`, {
        method: "PUT",
        body: JSON.stringify(lens),
      }),
    addView: (id: string, view: Pose & { name: string }) =>
      call<CameraWriteResult>(`/api/cameras/${encodeURIComponent(id)}/views`, {
        method: "POST",
        body: JSON.stringify(view),
      }),
    editView: (id: string, viewId: string, patch: Partial<Pose> & { name?: string }) =>
      call<CameraWriteResult>(
        `/api/cameras/${encodeURIComponent(id)}/views/${encodeURIComponent(viewId)}`,
        { method: "PATCH", body: JSON.stringify(patch) },
      ),
    deleteView: (id: string, viewId: string) =>
      call<CameraWriteResult>(
        `/api/cameras/${encodeURIComponent(id)}/views/${encodeURIComponent(viewId)}`,
        { method: "DELETE" },
      ),
    // Re-sync the camera list and restart the live pipeline (deepstream +
    // preview). Manual "apply / restart" for when an auto-restart failed.
    apply: () =>
      call<CameraWriteResult>("/api/cameras/apply", { method: "POST" }),
  },
  stores: {
    get: () => call<Stores>("/api/stores"),
    create: (body: StoreInput) =>
      call<Store>("/api/stores", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    update: (name: string, body: StoreUpdate) =>
      call<Store>(`/api/stores/${encodeURIComponent(name)}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    delete: (name: string) =>
      call<{ deleted: string; stores: Store[] }>(
        `/api/stores/${encodeURIComponent(name)}`,
        { method: "DELETE" },
      ),
    replay: (name: string) =>
      call<Store>(`/api/stores/${encodeURIComponent(name)}/sim/replay`, {
        method: "POST",
      }),
  },
  profiles: {
    list: () => call<CameraProfile[]>("/api/profiles"),
    create: (body: CameraProfileInput) =>
      call<CameraProfile>("/api/profiles", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    update: (id: string, body: CameraProfileInput) =>
      call<CameraProfile>(`/api/profiles/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    delete: (id: string) =>
      call<{ status: string }>(`/api/profiles/${encodeURIComponent(id)}`, {
        method: "DELETE",
      }),
  },
  notifications: {
    list: (opts: { unreadOnly?: boolean; includeDismissed?: boolean; limit?: number } = {}) => {
      const q = new URLSearchParams();
      if (opts.unreadOnly) q.set("unread_only", "true");
      if (opts.includeDismissed) q.set("include_dismissed", "true");
      if (opts.limit) q.set("limit", String(opts.limit));
      const qs = q.toString();
      return call<AppNotification[]>(`/api/notifications${qs ? `?${qs}` : ""}`);
    },
    unreadCount: () => call<{ unread: number }>("/api/notifications/unread-count"),
    markRead: (id: number) =>
      call<AppNotification>(`/api/notifications/${id}/read`, { method: "POST" }),
    dismiss: (id: number) =>
      call<AppNotification>(`/api/notifications/${id}/dismiss`, { method: "POST" }),
    readAll: () => call<{ changed: number }>("/api/notifications/read-all", { method: "POST" }),
  },
  limits: {
    get: () => call<CapacityLimits>("/api/limits"),
    update: (body: {
      max_ai_cameras?: number;
      max_ai_cameras_per_store?: number;
    }) =>
      call<CapacityLimits>("/api/limits", {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
  },
  nvrs: {
    list: () => call<Nvr[]>("/api/nvrs"),
    get: (id: string) => call<Nvr>(`/api/nvrs/${id}`),
    upsert: (nvr: NvrInput) =>
      call<Nvr>("/api/nvrs", {
        method: "POST",
        body: JSON.stringify(nvr),
      }),
    update: (nvr: NvrInput) =>
      call<Nvr>(`/api/nvrs/${nvr.id}`, {
        method: "PATCH",
        body: JSON.stringify(nvr),
      }),
    delete: (id: string) =>
      call<{ status: string }>(`/api/nvrs/${id}`, { method: "DELETE" }),
  },
  connectors: {
    list: () => call<Connector[]>("/api/connectors"),
    create: (body: { name: string; store: string }) =>
      call<{ connector: Connector; bundle: EnrollmentBundle }>("/api/connectors", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    reissue: (id: string) =>
      call<EnrollmentBundle>(`/api/connectors/${id}/enrollment`, { method: "POST" }),
    discover: (id: string) =>
      call<Connector>(`/api/connectors/${id}/discover`, { method: "POST" }),
    claim: (body: { serial: string; claim_code: string } & BoxPlacement) =>
      call<Connector>("/api/connectors/claim", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    assign: (id: string, body: BoxPlacement) =>
      call<Connector>(`/api/connectors/${id}/assign`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    release: (id: string) =>
      call<Connector>(`/api/connectors/${id}/release`, { method: "POST" }),
    setCustomer: (id: string, customer_id: number | null) =>
      call<Connector>(`/api/connectors/${id}/customer`, {
        method: "POST",
        body: JSON.stringify({ customer_id }),
      }),
    delete: (id: string) =>
      call<{ status: string; warning: string }>(`/api/connectors/${id}`, {
        method: "DELETE",
      }),
  },
  config: {
    get: () => call<Record<string, unknown>>("/api/config"),
    patch: (patch: Record<string, unknown>) =>
      call<{ status: string; restarted?: string[] }>("/api/config", {
        method: "PATCH",
        body: JSON.stringify(patch),
      }),
  },
  pipeline: {
    activity: () => call<PipelineActivity>("/api/pipeline/activity"),
  },
  admin: {
    storage: () => call<StorageUsage>("/api/admin/storage"),
    storagePolicy: () => call<{ policy: StoragePolicy }>("/api/admin/storage/policy"),
    storageHistory: (range: StorageHistoryRange = "30d") =>
      call<StorageHistory>(`/api/admin/storage/history?range=${range}`),
    updateStoragePolicy: (body: Partial<StoragePolicy>) =>
      call<{ policy: StoragePolicy }>("/api/admin/storage/policy", {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    purgeStorageDays: (days: number) =>
      call<StoragePurgeDaysResult>("/api/admin/storage/purge-days", {
        method: "POST",
        body: JSON.stringify({ days }),
      }),
    purgeData: () =>
      call<PurgeResult>("/api/admin/purge-data", { method: "POST" }),
    purgeCamera: (cameraId: string) =>
      call<ScopedPurgeResult>(
        `/api/admin/purge-camera/${encodeURIComponent(cameraId)}`,
        { method: "POST" },
      ),
    purgeStore: (store: string) =>
      call<ScopedPurgeResult>(
        `/api/admin/purge-store/${encodeURIComponent(store)}`,
        { method: "POST" },
      ),
  },
  frames: {
    delete: (frame_id: string) => call<{ status: string; frame_id: string }>(`/api/frames/${frame_id}`, { method: "DELETE" }),
  },
  tracks: {
    delete: (track_key: string) =>
      call<{ status: string; track_key: string }>(`/api/tracks/${encodeURIComponent(track_key)}`, { method: "DELETE" }),
  },
  events: {
    list: (filters: EventFilters = {}) => {
      const q = new URLSearchParams();
      for (const [k, v] of Object.entries(filters)) {
        if (v !== undefined && v !== null && v !== "") q.set(k, String(v));
      }
      const qs = q.toString();
      return call<EventsPage>(`/api/events${qs ? `?${qs}` : ""}`);
    },
  },
  operations: {
    triage: () => call<HostTriage>("/api/operations/triage"),
    boots: () => call<{ available: boolean; boots: HostBoot[]; error?: string }>(
      "/api/operations/boots",
    ),
    bootJournal: (bootId: string, tail = 100, kernelOnly = false) =>
      call<{ available: boolean; lines?: string[]; error?: string }>(
        `/api/operations/boot/${encodeURIComponent(bootId)}/journal?tail=${tail}&kernel_only=${kernelOnly}`,
      ),
    lastReboots: () =>
      call<{ available: boolean; entries: string[]; error?: string }>(
        "/api/operations/last-reboots",
      ),
    checklist: () => call<HostChecklist>("/api/operations/checklist"),
    instrumentation: () => call<HostChecklist>("/api/operations/instrumentation"),
    containers: () => call<{ available: boolean; containers: HostContainer[] }>(
      "/api/operations/containers",
    ),
    dockerLogs: (container: string, tail = 200) =>
      call<{ available: boolean; container?: string; lines?: string[]; error?: string }>(
        `/api/operations/docker-logs/${encodeURIComponent(container)}?tail=${tail}`,
      ),
    crashWindow: (params: { end?: string; window_min?: number; step_s?: number } = {}) => {
      const q = new URLSearchParams();
      if (params.end) q.set("end", params.end);
      if (params.window_min) q.set("window_min", String(params.window_min));
      if (params.step_s) q.set("step_s", String(params.step_s));
      const qs = q.toString();
      return call<CrashWindow>(`/api/operations/metrics/crash-window${qs ? `?${qs}` : ""}`);
    },
  },
  health: () => call<Record<string, boolean | string>>("/api/health"),
  workers: {
    gpus: () => call<WorkerGpu[]>("/api/workers/gpus"),
  },
  bandwidth: {
    sites: () => call<SiteBandwidth[]>("/api/bandwidth/sites"),
    setUplink: (store: string, uplink_mbps: number | null) =>
      call<{ uplink_mbps: number | null }>(`/api/bandwidth/sites/${encodeURIComponent(store)}`, {
        method: "PUT",
        body: JSON.stringify({ uplink_mbps }),
      }),
  },
  captionModels: {
    list: () => call<CaptionModels>("/api/vllm/models"),
    activate: (id: string) =>
      call<{ status: string; id: string; model: string }>("/api/vllm/models/active", {
        method: "POST",
        body: JSON.stringify({ id }),
      }),
  },
  // Returns true if the cookie is valid. Used to short-circuit the login
  // flow on app boot when the session is still good.
  authCheck: async () => {
    try {
      await call<void>("/api/auth/check");
      return true;
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) return false;
      throw e;
    }
  },
  metrics: {
    quickstats: () => call<QuickStats>("/api/metrics/quickstats"),
  },
  dashboard: {
    stats: (params: { range?: DashboardRange; camera_ids?: string[] }) => {
      const q = new URLSearchParams();
      if (params.range) q.set("range", params.range);
      for (const id of params.camera_ids ?? []) {
        q.append("camera_ids", id);
      }
      const qs = q.toString();
      return call<DashboardStats>(`/api/dashboard/stats${qs ? `?${qs}` : ""}`);
    },
    health: (scope?: Scope) =>
      call<FleetHealth>(`/api/dashboard/health${scopeQuery(scope)}`),
  },
  monitoring: {
    status: () => call<MonitoringStatus>("/api/monitoring/status"),
    cameras: (scope?: Scope) =>
      call<MonitoringCamera[]>(`/api/monitoring/cameras${scopeQuery(scope)}`),
    // Take or renew (same lease_id = heartbeat) a preview lease. The server
    // blocks until the stream is playable, so a returned path can be opened
    // immediately.
    acquireLease: (cameraId: string, body: PreviewLeaseRequest) =>
      call<PreviewLease>(
        `/api/monitoring/preview/${encodeURIComponent(cameraId)}/lease`,
        { method: "POST", body: JSON.stringify(body) },
      ),
    releaseLease: (
      cameraId: string,
      params: { mode: string; lease_id: string; profile?: string },
    ) => {
      const q = new URLSearchParams({
        mode: params.mode,
        lease_id: params.lease_id,
        profile: params.profile ?? "",
      });
      // keepalive so the release survives tab close / navigation.
      return fetch(
        `/api/monitoring/preview/${encodeURIComponent(cameraId)}/lease?${q}`,
        { method: "DELETE", credentials: "include", keepalive: true },
      ).catch(() => undefined);
    },
    modes: (cameraId: string) =>
      call<PreviewModes>(
        `/api/monitoring/preview/${encodeURIComponent(cameraId)}/modes`,
      ),
    setGlobalQuality: (quality: string) =>
      call<QualityInfo>("/api/monitoring/quality", {
        method: "PUT",
        body: JSON.stringify({ quality }),
      }),
    setCameraQuality: (cameraId: string, quality: string) =>
      call<QualityInfo>(
        `/api/monitoring/quality/${encodeURIComponent(cameraId)}`,
        { method: "PUT", body: JSON.stringify({ quality }) },
      ),
    // Advisory ceiling for the original (pass-through) stream. Not enforced on
    // the GPU — pass-through never re-encodes — but it is what warns a viewer
    // before they open something their machine cannot decode.
    setPassthroughMax: (quality: string) =>
      call<QualityInfo>("/api/monitoring/quality/passthrough-max", {
        method: "PUT",
        body: JSON.stringify({ quality }),
      }),
    // Grid-tile ("wall") encode profile; live wall tiles rebuild to match.
    setWallProfile: (height: number, bitrate: number) =>
      call<{ wall: WallProfile }>("/api/monitoring/wall", {
        method: "PUT",
        body: JSON.stringify({ height, bitrate }),
      }),
    // Bitrate clamps vs each camera's measured rate. null/0 resets a limit
    // to its config.yaml baseline.
    setTransmissionLimits: (body: {
      reencode_headroom?: number | null;
      min_bitrate_per_mpx?: number | null;
    }) =>
      call<{ limits: TransmissionLimits }>("/api/monitoring/limits", {
        method: "PUT",
        body: JSON.stringify(body),
      }),
    recover: (cameraId: string) =>
      call<{ source_id: string; recovering: boolean }>(
        `/api/monitoring/cameras/${encodeURIComponent(cameraId)}/recover`,
        { method: "POST" },
      ),
    session: (body: { session_id: string; client_id?: string; view?: string }) =>
      call<{ live_sessions: number; distinct_clients: number }>(
        "/api/monitoring/session",
        { method: "POST", body: JSON.stringify(body) },
      ),
  },
  dev: {
    // Debug-only: ask vLLM about a crop the page already holds. Stores
    // nothing server-side. A non-empty `prompt` is sent as the only text
    // with the image; blank uses the configured weapon-verify prompt.
    forceVerify: (imageB64: string, prompt?: string) =>
      call<{
        answer: string;
        confirmed: boolean;
        prompt_source: string;
        standalone: boolean;
        prompt_used: string;
        truncated: boolean;
      }>(
        "/api/dev/caption-feed/verify",
        {
          method: "POST",
          body: JSON.stringify({ image_b64: imageB64, prompt: prompt || null }),
        },
      ),
  },
  policies: {
    list: (scope?: Scope) => call<Policy[]>(`/api/policies${scopeQuery(scope)}`),
    create: (body: PolicyInput) =>
      call<Policy>("/api/policies", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    update: (id: string, body: PolicyInput) =>
      call<Policy>(`/api/policies/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    delete: (id: string) =>
      call<{ status: string; policy_id: string }>(
        `/api/policies/${encodeURIComponent(id)}`,
        { method: "DELETE" },
      ),
    // Live JPEG still for drawing a region box. Returns the fetch URL; the
    // component loads it as a blob (auth cookie) into an <img>.
    snapshotUrl: (cameraId: string) =>
      `/api/policies/snapshot/${encodeURIComponent(cameraId)}`,
  },
  alerts: {
    weapon: (scope?: Scope, page?: WeaponAlertsPage) =>
      call<WeaponAlertsPanel>(
        `/api/alerts/weapon${scopeQuery(scope, {
          limit: page?.limit ?? ALERTS_PAGE_SIZE,
          pending_offset: page?.pendingOffset,
          confirmed_offset: page?.confirmedOffset,
          history_offset: page?.historyOffset,
        })}`,
      ),
    dismissWeapon: (alertId: string, fromHistory = false) =>
      call<{ status: string; alert_id: string }>(
        `/api/alerts/weapon/${encodeURIComponent(alertId)}?from_history=${fromHistory ? "true" : "false"}`,
        { method: "DELETE" },
      ),
    // Scoped: clearing while looking at one store must not wipe another's.
    dismissAllWeapon: (scope?: Scope) =>
      call<{ status: string; count: number }>(
        `/api/alerts/weapon${scopeQuery(scope)}`,
        { method: "DELETE" },
      ),
  },
};

export type MonitoringStatus = {
  transport: "webrtc";
  webrtc_base: string;
  hls_base: string;
  deepstream: string; // "ready" or a human-readable unavailability note
  encoders?: { active: number; max: number | string };
  // Real viewer counts from the pipeline's session bookkeeping. `sessions` is
  // open viewer pages, `distinct_clients` the machines they are on, and
  // `lease_holders` the encoder seats held (a 16-tile wall is one person with
  // sixteen). None of these is derivable from the encoder count.
  viewers?: {
    sessions: number;
    distinct_clients: number;
    lease_holders: number;
  };
  quality?: QualityInfo;
  wall_profile?: WallProfile;
  limits?: TransmissionLimits;
  // Analysis (mux) geometry; the "Detections" overlay is cut from these
  // buffers so its encode cannot exceed this resolution.
  mux?: { width: number; height: number };
};

export type WallProfile = { name: string; height: number; bitrate: number };

export type TransmissionLimits = {
  reencode_headroom: number; // encode bitrate cap = this x measured camera rate
  min_bitrate_per_mpx: number; // bits/s per Mpx floor under that cap
  config_reencode_headroom: number;
  config_min_bitrate_per_mpx: number;
};

export type MonitoringCamera = {
  id: string;
  name: string;
  store?: string;
  /** False when this simulation store's Live video switch is off. */
  sim_live?: boolean;
  /** Play-once clip reached EOF; same kind of stop as Live video is off. */
  sim_finished?: boolean;
  role: "ai" | "view_only" | string;
  state:
    | "pending"
    | "streaming"
    | "degraded"
    | "dead"
    | "rejected"
    | "ended"
    | "over_capacity"
    | "unknown"
    | string;
  healthy: boolean;
  health_reason: string;
  recovering: boolean;
  // DeepStream lifetime rebuild count; bumps when a recover finishes.
  recoveries?: number;
  seconds_since_frame: number | null;
  quality: string; // per-camera preset override; "" = global default
  // Whether the recorder offers a second (lower-bitrate) stream.
  has_sub_stream?: boolean;
  /** The location lets Original fall back to HLS when WebRTC cannot play it. */
  hls_fallback?: boolean;
  // Configured source-resolution ceiling (px of height; 0 = unlimited) and
  // what the decoder actually reported.
  max_height?: number;
  native_width?: number | null;
  native_height?: number | null;
  // True when the camera decoded above its ceiling and was detached.
  resolution_rejected?: boolean;
  // True when the camera is configured but not analysed because the fleet is
  // over its AI limits.
  over_capacity?: boolean;
  /** A video processing server currently has this camera. */
  analysed?: boolean;
  /** No video processing server is online: live view plays the camera's own
   *  stream, unanalysed. */
  direct_live?: boolean;
  // Fisheye geometry for in-browser dewarp: `lens` on the fisheye itself; on
  // one of its views, `view` plus the fisheye's lens (`parent_lens`).
  lens?: Lens | null;
  parent_id?: string | null;
  view?: Pose | null;
  parent_lens?: Lens | null;
};

export type PreviewMode = "passthrough" | "transcode" | "annotated";

// Which of the camera's own encodings pass-through carries. The re-encoded
// modes always come from the main stream.
export type PreviewStream = "main" | "sub";

export type PreviewLeaseRequest = {
  mode: PreviewMode;
  // "" = camera override / global default; "wall" = grid tile profile; or a
  // preset name to pin. Echo the returned `encode` back on heartbeats.
  profile?: string;
  lease_id?: string;
  stream?: PreviewStream;
};

export type PreviewLease = {
  source_id: string;
  mode: PreviewMode;
  encode: string;
  path: string;
  lease_id: string;
  lease_ttl_s: number;
  ready: boolean;
  encoded: boolean;
  resolution: string;
  bitrate: number | null;
  note: string | null;
  rtsp: string;
  stream?: PreviewStream;
  has_sub_stream?: boolean;
  detail?: string;
  failure?: "source" | "encoder" | "server" | "playback";
  retryable?: boolean;
  sim_live?: boolean;
  sim_finished?: boolean;
  // Pass-through only: the path's measured codec, and whether WebRTC can carry
  // it (false for H.264 with B-frames). Absent until the gateway has probed.
  codec?: string;
  webrtc_safe?: boolean;
};

export type PreviewModes = {
  source_id: string;
  modes: Record<
    string,
    {
      available: boolean;
      detail: string;
      resolution: string;
      gpu_cost: string;
      webrtc_safe: boolean;
      warning?: string;
      codec?: string;
    }
  >;
};

export type QualityInfo = {
  presets: Record<string, { height: number; bitrate: number }>;
  default: string;
  passthrough_max: string;
  per_source: Record<string, string>;
};

export type LiveDetection = {
  camera_id: string;
  track_id: number;
  cls: number;
  conf: number;
  // Normalized xyxy in [0, 1] against the nvstreammux output resolution.
  bbox: [number, number, number, number];
  pts_ns: number;
  ts_ms: number;
  received_at_ms?: number;
};
